import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Line } from 'react-chartjs-2';
import { useChartTheme, tooltipStyle, withAlpha, crosshairPlugin } from './chartSetup';

// Funding rate over time for one pair: the area above zero is tinted positive, below it negative.

const Wrap = styled.div`
  border: 1px solid var(--separator);
  border-radius: var(--radius-md);
  padding: 16px 16px 8px;
`;

const Title = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.012em;
  color: var(--text);
  margin: 0 0 12px 4px;
`;

const Canvas = styled.div`
  height: 260px;
`;

const recordTime = (record) => Number(record.timestamp || record.fundingTime || record.timepoint || record.timePoint || record.ts);
const recordRate = (record) => parseFloat(record.funding_rate ?? record.fundingRate);

const signed = (value) => `${value > 0 ? '+' : ''}${value.toFixed(6)}%`;

function RateHistoryChart({ records, title, dateLocale = 'en-US' }) {
  const theme = useChartTheme();

  const points = useMemo(
    () =>
      (records || [])
        .map((record) => ({ time: recordTime(record), value: recordRate(record) * 100 }))
        .filter((point) => point.time > 1e12 && Number.isFinite(point.value))
        .sort((a, b) => a.time - b.time),
    [records]
  );

  if (points.length < 2) return null;

  const shortDate = new Intl.DateTimeFormat(dateLocale, { month: 'short', day: 'numeric' });
  const fullDate = new Intl.DateTimeFormat(dateLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const data = {
    labels: points.map((point) => shortDate.format(point.time)),
    datasets: [
      {
        data: points.map((point) => point.value),
        borderColor: theme.accent,
        borderWidth: 2,
        tension: 0.35,
        pointRadius: 0,
        pointHitRadius: 12,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: theme.accent,
        pointHoverBorderColor: theme.surface,
        pointHoverBorderWidth: 2,
        fill: {
          target: 'origin',
          above: withAlpha(theme.positive, 0.14),
          below: withAlpha(theme.negative, 0.14),
        },
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      fpCrosshair: { color: theme.axis },
      tooltip: {
        ...tooltipStyle(theme),
        displayColors: false,
        callbacks: {
          title: (items) => (items.length ? fullDate.format(points[items[0].dataIndex].time) : ''),
          label: (context) => signed(context.parsed.y),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { color: theme.axis },
        ticks: {
          color: theme.textSecondary,
          font: { family: theme.font, size: 11 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7,
        },
      },
      y: {
        grid: {
          color: (context) => (context.tick && context.tick.value === 0 ? theme.axis : theme.grid),
          drawTicks: false,
        },
        border: { display: false },
        ticks: {
          color: theme.textSecondary,
          font: { family: theme.font, size: 11 },
          padding: 8,
          maxTicksLimit: 6,
          callback: (value) => `${parseFloat(Number(value).toFixed(4))}%`,
        },
      },
    },
  };

  return (
    <Wrap>
      <Title>{title}</Title>
      <Canvas>
        <Line data={data} options={options} plugins={[crosshairPlugin]} role="img" aria-label={title} />
      </Canvas>
    </Wrap>
  );
}

export default RateHistoryChart;
